/**
 * Subscription Service - Handles profile subscription and plan gating operations
 */

import { API_BASE_URL } from '../config/api';
import { getSession } from '../utils/authUtils';
import logger from '../utils/logger';

const SUBSCRIPTION_API_URL = `${API_BASE_URL}/subscription`;

/**
 * Get auth headers
 */
function getAuthHeaders() {
  const session = getSession();
  const token = session?.token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

function buildSubscriptionCheckPayload(profileKey) {
  const input = profileKey ? String(profileKey) : '';
  let accountId = null;
  let locationId = null;

  if (input.includes('/locations/')) {
    const accountMatch = input.match(/\/accounts\/([^/]+)\//);
    const locationMatch = input.match(/\/locations\/([^/]+)/);
    accountId = accountMatch ? accountMatch[1] : null;
    locationId = locationMatch ? locationMatch[1] : null;
  } else if (input.includes(':')) {
    const parts = input.split(':');
    if (parts.length === 2) {
      accountId = parts[0] || null;
      locationId = parts[1] || null;
    }
  } else if (/^\d+$/.test(input)) {
    // If input is just a number, treat it as locationId
    locationId = input;
  }

  // Always include locationId if we have it - this helps backend find admin-assigned plans
  if (locationId) {
    return { profileKey, accountId, locationId };
  }

  return { profileKey };
}

/**
 * Check subscription status for a profile
 * @param {string} profileKey - The profile key (account:location format)
 * @returns {Promise<SubscriptionStatus>}
 */
export async function checkSubscription(profileKey) {
  try {
    console.log('[SubscriptionService] 🔍 Checking subscription for profileKey:', profileKey);

    const payload = buildSubscriptionCheckPayload(profileKey);
    console.log('[SubscriptionService] 📤 Sending payload to backend:', payload);

    const response = await fetch(`${SUBSCRIPTION_API_URL}/check`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    console.log('[SubscriptionService] 📥 Received response:', {
      hasSubscription: result.hasSubscription,
      planCode: result.planCode,
      status: result.status,
      source: result.source,
      fullResponse: result
    });

    return result;
  } catch (error) {
    logger.error('Error checking subscription:', error);
    return {
      success: false,
      hasSubscription: false,
      planCode: null,
      message: 'Unable to check subscription status'
    };
  }
}

/**
 * Check if a specific feature is available for a profile
 * @param {string} profileKey - The profile key
 * @param {string} feature - Feature name to check
 * @returns {Promise<{isAllowed: boolean, requiredPlan: string}>}
 */
export async function checkFeatureAccess(profileKey, feature) {
  try {
    const response = await fetch(`${SUBSCRIPTION_API_URL}/check-feature`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ profileKey, feature })
    });

    return await response.json();
  } catch (error) {
    logger.error('Error checking feature access:', error);
    return {
      success: false,
      isAllowed: false,
      message: 'Unable to check feature access'
    };
  }
}

/**
 * Get all subscriptions for the current user
 * @returns {Promise<{subscriptions: Array}>}
 */
export async function getMySubscriptions() {
  try {
    const response = await fetch(`${SUBSCRIPTION_API_URL}/my-subscriptions`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    return await response.json();
  } catch (error) {
    logger.error('Error fetching subscriptions:', error);
    return {
      success: false,
      subscriptions: [],
      message: 'Unable to fetch subscriptions'
    };
  }
}

/**
 * Get subscription for a specific profile
 * @param {string} profileKey - The profile key
 * @returns {Promise<SubscriptionStatus>}
 */
export async function getProfileSubscription(profileKey) {
  try {
    const encodedKey = encodeURIComponent(profileKey);
    const response = await fetch(`${SUBSCRIPTION_API_URL}/profile/${encodedKey}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    return await response.json();
  } catch (error) {
    logger.error('Error fetching profile subscription:', error);
    return {
      success: false,
      hasSubscription: false,
      planCode: null,
      message: 'Unable to fetch profile subscription'
    };
  }
}

/**
 * Plan feature definitions (client-side copy for UI)
 * Only 3 plans: basic, advance, pro
 * Profiles without a plan (null) should be prompted to purchase
 */
export const PLAN_FEATURES = {
  basic: {
    label: 'Basic',
    color: '#10B981',
    features: {
      dashboard: true,
      viewReviews: true,
      replyReviews: true,
      createPosts: true,
      schedulePosts: true,
      aiPostGeneration: true,
      aiReviewReplies: true,
      heatmapAnalysis: false,
      competitorAnalysis: true,   // Basic has access with 15-day cooldown
      profileStrength: true,
      freeWebsite: true,
      edms: true,
      autoReply: false,
      taskManagement: true,
      analytics: false,
      // Navigation features (for sidebar access)
      performance: true,
      reputation: true,
      'task-management': true,
      'get-edms': true,
      'free-website': true,
      'refer-earn': true,
      'monthly-plan': true,
    }
  },
  advance: {
    label: 'Advance',
    color: '#3B82F6',
    features: {
      dashboard: true,
      viewReviews: true,
      replyReviews: true,
      createPosts: true,
      schedulePosts: true,
      aiPostGeneration: true,
      aiReviewReplies: true,
      heatmapAnalysis: true,
      competitorAnalysis: true,
      profileStrength: true,
      freeWebsite: true,
      edms: true,
      autoReply: true,
      taskManagement: true,
      analytics: true,
      // Navigation features (for sidebar access)
      performance: true,
      reputation: true,
      'task-management': true,
      'get-edms': true,
      'free-website': true,
      'refer-earn': true,
      'monthly-plan': true,
    }
  },
  pro: {
    label: 'Pro',
    color: '#8B5CF6',
    features: {
      dashboard: true,
      viewReviews: true,
      replyReviews: true,
      createPosts: true,
      schedulePosts: true,
      aiPostGeneration: true,
      aiReviewReplies: true,
      heatmapAnalysis: true,
      competitorAnalysis: true,
      profileStrength: true,
      freeWebsite: true,
      edms: true,
      autoReply: true,
      taskManagement: true,
      analytics: true,
      // Navigation features (for sidebar access)
      performance: true,
      reputation: true,
      'task-management': true,
      'get-edms': true,
      'free-website': true,
      'refer-earn': true,
      'monthly-plan': true,
    }
  }
};

/**
 * Competitor Analysis cooldown days per plan
 * Profiles without a plan should not have access (handled by hasFeature)
 */
export const COMPETITOR_COOLDOWN_DAYS = {
  basic: 15,    // 15 days cooldown
  advance: 7,   // 7 days cooldown
  pro: 3        // 3 days cooldown
};

/**
 * Plan-based limits for features
 * Basic: Limited access, Advance: Medium access, Pro: Full access
 */
export const PLAN_LIMITS = {
  // Heatmap access: Basic cannot use, Advance and Pro can
  heatmapAccess: {
    none: false,
    basic: false,
    advance: true,
    pro: true
  },
  // Number of competitor GMBs visible in analysis results
  competitorGmbsVisible: {
    none: 1,    // No plan = same as basic
    basic: 1,
    advance: 3,
    pro: 5
  },
  // Number of keywords for tracking
  keywordTracking: {
    none: 2,
    basic: 2,
    advance: 5,
    pro: 10
  }
};

/**
 * Get heatmap access for a plan
 */
export function hasHeatmapAccess(planCode) {
  return PLAN_LIMITS.heatmapAccess[planCode?.toLowerCase()] ?? PLAN_LIMITS.heatmapAccess.pro;
}

/**
 * Get competitor GMBs visible limit for a plan
 */
export function getCompetitorGmbsLimit(planCode) {
  return PLAN_LIMITS.competitorGmbsVisible[planCode?.toLowerCase()] ?? PLAN_LIMITS.competitorGmbsVisible.basic;
}

/**
 * Get keyword tracking limit for a plan
 */
export function getKeywordTrackingLimit(planCode) {
  return PLAN_LIMITS.keywordTracking[planCode?.toLowerCase()] ?? PLAN_LIMITS.keywordTracking.pro;
}

/**
 * Get plan info by code
 */
export function getPlanInfo(planCode) {
  return PLAN_FEATURES[planCode?.toLowerCase()] || PLAN_FEATURES.basic;
}

/**
 * Check if a feature is available for a plan (client-side)
 * Uses the PLAN_FEATURES definition to check access
 */
export function hasFeature(planCode, feature) {
  // Default to basic plan if no plan specified
  const effectivePlan = planCode || 'basic';
  const plan = PLAN_FEATURES[effectivePlan?.toLowerCase()] || PLAN_FEATURES.basic;
  return plan.features?.[feature] ?? false;
}

/**
 * Get competitor analysis cooldown days for a plan
 * Defaults to 15 days (basic plan) if plan not recognized
 */
export function getCompetitorCooldownDays(planCode) {
  // If no plan assigned, default to basic plan's 15 days (not blocking with 999)
  if (!planCode || planCode === 'none' || planCode === 'null') return 15;
  return COMPETITOR_COOLDOWN_DAYS[planCode?.toLowerCase()] || 15; // Default to basic's 15 days
}

/**
 * Get the minimum required plan for a feature
 */
export function getRequiredPlan(feature) {
  if (hasFeature('basic', feature)) return 'basic';
  if (hasFeature('advance', feature)) return 'advance';
  if (hasFeature('pro', feature)) return 'pro';
  return 'basic'; // Default to basic as minimum required
}

/**
 * Feature display names for UI
 */
export const FEATURE_LABELS = {
  dashboard: 'Dashboard',
  viewReviews: 'View Reviews',
  replyReviews: 'Reply to Reviews',
  createPosts: 'Create Posts',
  schedulePosts: 'Schedule Posts',
  aiPostGeneration: 'AI Post Generation',
  aiReviewReplies: 'AI Review Replies',
  heatmapAnalysis: 'Heatmap Analysis',
  competitorAnalysis: 'Competitor Analysis',
  profileStrength: 'Profile Strength',
  freeWebsite: 'Free Website',
  edms: 'Marketing Materials',
  autoReply: 'Auto Reply',
  taskManagement: 'Task Management',
  analytics: 'Advanced Analytics',
  'monthly-plan': 'Monthly Plan',
};

/**
 * Cooldown days by plan for competitor analysis
 */
export const COMPETITOR_ANALYSIS_COOLDOWNS = {
  none: 30,
  basic: 15,
  advance: 7,
  pro: 3
};

/**
 * Check cooldown status for a feature (competitor analysis)
 * @param {string} locationId - The GMB location ID
 * @param {string} feature - Feature name (e.g., 'competitorAnalysis')
 * @returns {Promise<{canRunNow: boolean, cooldownDays: number, daysRemaining: number, planCode: string}>}
 */
export async function checkCooldown(locationId, feature = 'competitorAnalysis') {
  try {
    const response = await fetch(`${SUBSCRIPTION_API_URL}/cooldown`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ locationId, feature })
    });

    return await response.json();
  } catch (error) {
    logger.error('Error checking cooldown:', error);
    return {
      success: false,
      canRunNow: false, // Don't allow on error - require plan check
      cooldownDays: 15,
      daysRemaining: 0,
      planCode: null,
      message: 'Unable to check cooldown status'
    };
  }
}

const SubscriptionService = {
  checkSubscription,
  checkFeatureAccess,
  getMySubscriptions,
  getProfileSubscription,
  checkCooldown,
  PLAN_FEATURES,
  PLAN_LIMITS,
  getPlanInfo,
  hasFeature,
  getRequiredPlan,
  getCompetitorCooldownDays,
  hasHeatmapAccess,
  getCompetitorGmbsLimit,
  getKeywordTrackingLimit,
  FEATURE_LABELS,
  COMPETITOR_COOLDOWN_DAYS
};

export default SubscriptionService;
